import React, { useEffect, useState, FC } from "react";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import Avatar from "../../components/ui/Avatar";
import Select from "../../components/ui/Select";
import { Application, JobOption } from "../../types/components";
import { useAuth } from "../../hooks/useAuth";
import {
  CopmanyUser,
  JobApplicationStatus,
  JobPostResponse,
  JobApplicantsResponse,
} from "../../types/api";
import jobService from "../../services/job.service";
import ApplicationStatusFlow from "../../components/Job/ApplicationStatusFlow";
import authService from "../../services/auth.service";
import Loader from "../../components/ui/Loader";

const JobApplications: FC = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobPosts, setJobPosts] = useState<JobPostResponse[]>([]);
  const [selectedApplicant, setSelectedApplicant] =
    useState<Application | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const fetchApplications = async () => {
    if (!(user && user.role === "Company" && (user as CopmanyUser).companyId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const companyId = (user as CopmanyUser).companyId as string;
      const jobPostsRes = await jobService.getAllCompanyJobPosts(companyId);

      if (
        jobPostsRes.status === "success" &&
        jobPostsRes.data &&
        jobPostsRes.data.posts
      ) {
        setJobPosts(jobPostsRes.data.posts);
        const allApplications: Application[] = [];
        for (const post of jobPostsRes.data.posts) {
          const applicantsRes = await jobService.getJobApplicants(
            post.postId,
            1,
            100,
          );
          if (
            applicantsRes.status === "success" &&
            applicantsRes.data &&
            applicantsRes.data.items
          ) {
            const postApplications = applicantsRes.data.items
              .filter((a) => a.applicationId !== undefined)
              .map((a: JobApplicantsResponse) => ({
                id: a.applicationId!,
                jobTitle: post.jobTitle || "Unknown Job",
                candidate: {
                  name: a.userName || "Unknown User",
                  email: "dummy@email.com", // Placeholder
                  phone: "123-456-7890", // Placeholder
                  experience: "5 years", // Placeholder
                  location: "dummy location", // Placeholder
                  avatar: null,
                },
                status: a.status || "Applied",
                appliedDate: "2024-01-01", // Placeholder
                resume: a.resumeUrl || "",
                coverLetter: "Lorem ipsum...", // Placeholder
                skills: ["React", "TypeScript"], // Placeholder
                education: "B.Sc. in Computer Science", // Placeholder
                currentCompany: "Tech Corp", // Placeholder
                expectedSalary: "100,000", // Placeholder
                noticePeriod: "1 month", // Placeholder
              }));
            allApplications.push(...postApplications);
          }
        }
        setApplications(allApplications);
      } else {
        setError("Failed to load job posts");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [user]);

  const [selectedStatus, setSelectedStatus] = useState<
    JobApplicationStatus | "All"
  >("All");
  const [selectedJob, setSelectedJob] = useState<JobOption>("All");
  const statusOptions: Array<JobApplicationStatus | "All"> = [
    "All",
    "Invited",
    "Bookmarked",
    "ViewedByCandidate",
    "ViewedByEmployer",
    "NotInterested",
    "Applied",
    "WithdrawnByCandidate",
    "UnderReview",
    "Shortlisted",
    "ReachedOut",
    "InterviewScheduled",
    "InterviewNo_Show",
    "InterviewCompleted",
    "Rejected",
    "Offered",
    "OfferedAccepted",
    "OfferDeclined",
    "Hired",
    "OnHold",
    "Waitlisted",
  ];
  const jobOptions: JobOption[] = [
    "All",
    ...jobPosts.map((post) => `${post.jobTitle} / ${post.postId}`),
  ];

  const filteredApplications = applications.filter((app) => {
    const statusMatch =
      selectedStatus === "All" || app.status === selectedStatus;
    const jobMatch =
      selectedJob === "All" ||
      `${app.jobTitle} / ${app.id}`.startsWith(selectedJob.split(" / ")[0]);
    return statusMatch && jobMatch;
  });

  const getStatusColor = (status: Application["status"]): string => {
    switch (status) {
      case "Invited":
        return "bg-gray-100 text-gray-800";
      case "Bookmarked":
        return "bg-gray-100 text-gray-800";
      case "ViewedByCandidate":
        return "bg-gray-100 text-gray-800";
      case "ViewedByEmployer":
        return "bg-gray-100 text-gray-800";
      case "NotInterested":
        return "bg-red-100 text-red-800";
      case "Applied":
        return "bg-primary-lightGreen/20 text-primary-lightGreen";
      case "WithdrawnByCandidate":
        return "bg-red-100 text-red-800";
      case "UnderReview":
        return "bg-yellow-100 text-yellow-800";
      case "Shortlisted":
        return "bg-yellow-100 text-yellow-800";
      case "ReachedOut":
        return "bg-yellow-100 text-yellow-800";
      case "InterviewScheduled":
        return "bg-yellow-100 text-yellow-800";
      case "InterviewNo_Show":
        return "bg-yellow-100 text-yellow-800";
      case "InterviewCompleted":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Offered":
        return "bg-primary-lightGreen/20 text-primary-lightGreen";
      case "OfferedAccepted":
        return "bg-green-100 text-green-800";
      case "OfferDeclined":
        return "bg-purple-100 text-purple-800";
      case "Hired":
        return "bg-green-100 text-green-800";
      case "OnHold":
        return "bg-gray-100 text-gray-800";
      case "Waitlisted":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleUpdateStatusClick = (applicant: Application) => {
    setSelectedApplicant(applicant);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedApplicant(null);
  };

  const handleStageSelect = async (newStatus: JobApplicationStatus) => {
    if (!selectedApplicant) return;

    try {
      const { userId } = await authService.getMe();
      await jobService.updateJobApplication({
        applicationId: selectedApplicant.id,
        newStatus: newStatus,
        modifiedBy: userId,
      });
      fetchApplications();
      handleModalClose();
      setToast({ message: "application status updated", type: 'success' });  
    } catch (error) {
      console.error("Failed to update job application status:", error);
      // setError("Failed to update job application status");
      setToast({ message: "Failed to update application status", type: 'error' });  
    }
  };

  return (
    <div className="space-y-6 mt-6 md:mt-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Applications</h1>
          <p className="text-gray-600">
            Manage and review candidate applications
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Stats Cards */}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-wrap gap-6">
          <div className="flex-1 min-w-[200px]">
            <Select
              label="Filter by Status"
              options={statusOptions.map((status) => ({
                value: status,
                label: status,
              }))}
              value={selectedStatus}
              onChange={(value) =>
                setSelectedStatus(value as JobApplicationStatus)
              }
              placeholder="Select status..."
              showSearchButton={false}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select
              label="Filter by Job"
              options={jobOptions.map((job) => ({ value: job, label: job }))}
              value={selectedJob}
              onChange={(value) => setSelectedJob(value as JobOption)}
              placeholder="Select job..."
              showSearchButton={false}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {loading && (
          <div className="text-center">
            <Loader />
            <p className="mt-4 text-gray-600">Loading Applications...</p>
          </div>
        )}
        {(loading ? [] : filteredApplications).map((application) => (
          <div
            key={application.id}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <Avatar
                  name={application.candidate.name}
                  size="lg"
                  src={application.candidate.avatar}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {application.candidate.name}
                    </h3>
                    <Badge className={getStatusColor(application.status)}>
                      {application.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Applied for{" "}
                    <span className="font-medium">{application.jobTitle}</span>{" "}
                    on {application.appliedDate}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    {/* Candidate details */}
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(application.resume, "_blank")}
                  disabled={application.resume == ""}
                >
                  View Resume
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleUpdateStatusClick(application)}
                >
                  Update Status
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && filteredApplications.length === 0 && (
        <div className="text-center py-12">{/* No applications found */}</div>
      )}
      {isModalOpen && selectedApplicant && (
        <ApplicationStatusFlow
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onStageSelect={handleStageSelect}
          currentStatus={selectedApplicant.status || "Invited"}
          userRole={user?.role}
        />
      )}
    </div>
  );
};

export default JobApplications;
